import api from './../api/index';

export default {
    init: ({ state, dispatch, commit }, {
        lang,
        broker,
        currency,
        platformCode,
        cacheTtl,
        source,
        headerOffset
    }) => {
        commit('setLang', { lang });
        commit('setBroker', { broker });
        commit('setCurrency', { currency });
        commit('setCacheTtl', { cacheTtl: typeof cacheTtl === 'string' ? Number(cacheTtl) : cacheTtl });
        commit('setSource', { source });
        commit('setHeaderOffset', { headerOffset });
        dispatch('resetPlatforms', { platformCode });
    },

    getPlatforms: ({ state, commit }) => {
        const { source, lang, broker, cacheTtl } = state;
        return api.getPlatforms({ source, lang, broker, cacheTtl });
    },

    getInstruments: ({ state, commit }, { platformCode }) => {
        const { source, lang, broker, currency, cacheTtl } = state;
        return api.getInstruments({ source, lang, broker, currency, cacheTtl, platformCode });
    },

    getColumns: ({ state, commit }, { dataGridId }) => {
        const { source, lang, currency, cacheTtl } = state;
        return api.getColumns({ source, lang, currency, cacheTtl, dataGridId });
    },

    resetPlatforms: ({ state, dispatch, commit }, { platformCode }) => {
        const updateId = Date.now();
        commit('setPlatformsLoadingId', { updateId });
        return dispatch('getPlatforms')
            .then(platforms => {
                if (updateId !== state.platformsLoadingId) {
                    return;
                }
                if (!platformCode || !platforms.find(platform => platform.Code === platformCode)) {
                    platformCode = platforms[0].Code;
                }
                commit('setPlatforms', { platforms });
                commit('setPlatformCode', { platformCode });
                commit('setPlatformsLoadingId', { updateId: null });
            });
    },

    resetGroups: ({ state, commit }, { selectedPlatform, groupId }) => {
        const updateId = Date.now();
        commit('setGroupsLoadingId', { updateId });
        return Promise.resolve(selectedPlatform.InstrGroups)
            .then(groups => {
                if (updateId !== state.groupsLoadingId) {
                    return;
                }
                if (!groupId || !groups.find(group => group.Id === groupId)) {
                    groupId = groups[0].Id;
                }
                commit('setGroups', { groups });
                commit('setGroupId', { groupId });
                commit('setGroupsLoadingId', { updateId: null });
            });
    },

    resetSubgroups: ({ state, commit }, { selectedGroup, subgroupsIds }) => {
        const updateId = Date.now();
        commit('setSubgroupsLoadingId', { updateId });
        return Promise.resolve(selectedGroup.InstrSubGroups)
            .then(subgroups => {
                if (updateId !== state.subgroupsLoadingId) {
                    return;
                }
                if (!subgroupsIds || !subgroups.filter(subgroup => subgroupsIds.includes(subgroup.Id)).length) {
                    subgroupsIds = subgroups.map(subgroup => subgroup.Id);
                }
                commit('setSubgroups', { subgroups });
                commit('setSubgroupsIds', { subgroupsIds });
                commit('setSubgroupsLoadingId', { updateId: null });
            });
    },

    resetColumns: ({ state, dispatch, commit }, { dataGridId, columnsFieldsNames }) => {
        const updateId = Date.now();
        commit('setColumnsLoadingId', { updateId });
        return dispatch('getColumns', { dataGridId })
            .then(columns => {
                if (updateId !== state.columnsLoadingId) {
                    return;
                }
                const columnsFieldsNames = columns.filter(column => column.IsDefaultField).map(column => column.MatchingFieldName);
                commit('setColumns', { columns });
                commit('setColumnsFieldsNames', { columnsFieldsNames });
                commit('setColumnsLoadingId', { updateId: null });
            });
    },

    resetInstruments: ({ state, dispatch, commit }, { platformCode }) => {
        const updateId = Date.now();
        commit('setInstrumentsLoadingId', { updateId });
        return dispatch('getInstruments', { platformCode })
            .then(instruments => {
                if (updateId !== state.instrumentsLoadingId) {
                    return;
                }
                commit('setInstruments', { instruments });
                commit('setInstrumentsLoadingId', { updateId: null });
            });
    },

    resetFilter: ({ commit }) => {
        commit('setFilter', {filter: ''});
    },

    resetSortKey: ({ getters, commit }) => {
        commit('setSortKey', { sortKey: getters.selectedColumns[0].MatchingFieldName });
    },

    resetSortIsAscending: ({ commit }) => {
        commit('setSortIsAscending', { sortIsAscending: true });
    },

    setColumnsToShow: ({ getters, commit }) => {
        commit('setColumnsToShow', { columns: getters.selectedColumns });
    },

    setInstrumentsToShow: ({ state, getters, commit }) => {
        const filterByGroup = instrument => instrument.GroupIds.includes(state.groupId);
        const filterBySubgroups = instrument => {
            if (!state.subgroups.length) {
                return true;
            }
            if (!state.subgroupsIds.length) {
                return false;
            }
            return instrument.SubGroupIds.some(subgroupId => state.subgroupsIds.includes(subgroupId));
        };
        const filterByFilter = instrument => {
            return (instrument[getters.selectedColumns[0].MatchingFieldName].Text.toLowerCase().includes(state.filter.toLowerCase()) ||
                instrument.LocalizedName.Text.toLowerCase().includes(state.filter.toLowerCase()));
        };
        const sortByKey = (prev, next) => {
            if ((next[state.sortKey].Text || '').toLowerCase() < (prev[state.sortKey].Text || '').toLowerCase()) {
                if (state.sortIsAscending) {
                    return 1;
                }
                return -1;
            } else if ((next[state.sortKey].Text || '').toLowerCase() > (prev[state.sortKey].Text || '').toLowerCase()) {
                if (state.sortIsAscending) {
                    return -1;
                }
                return 1;
            }
            if (state.sortKey === getters.selectedColumns[0].MatchingFieldName) {
                return 0;
            } else {
                if (next[getters.selectedColumns[0].MatchingFieldName].Text.toLowerCase() < prev[getters.selectedColumns[0].MatchingFieldName].Text.toLowerCase()) {
                    if (state.sortIsAscending) {
                        return 1;
                    }
                    return -1;
                } else if (next[getters.selectedColumns[0].MatchingFieldName].Text.toLowerCase() > prev[getters.selectedColumns[0].MatchingFieldName].Text.toLowerCase()) {
                    if (state.sortIsAscending) {
                        return -1;
                    }
                    return 1;
                }
                return 0;
            }
        };

        const instruments = state.instruments
            .filter(filterByGroup)
            .filter(filterBySubgroups)
            .filter(filterByFilter)
            .slice().sort(sortByKey);
        commit('setInstrumentsToShow', { instruments });
    }
};



// WEBPACK FOOTER //
// ./src/store/actions.js